import { useAuth, useSignIn, useSignUp, useUser } from '@clerk/clerk-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FiCheck } from 'react-icons/fi';
import { Input } from '@/components/Input';
import { ApiError, apiFetch, extractApiErrorMessage } from '@/lib/api';
import { track } from '@/lib/analytics';

type Flow = 'email' | 'code' | 'claiming' | 'ready';
type Attempt = 'sign-in' | 'sign-up' | 'attach-email';

function clerkMessage(error: unknown, fallback: string): string {
  const value = error as {
    errors?: { code?: string; message?: string; longMessage?: string }[];
    message?: string;
  };
  return (
    value?.errors?.[0]?.longMessage ??
    value?.errors?.[0]?.message ??
    value?.message ??
    fallback
  );
}

export function DivorceAccountGate({
  claimToken,
  ready,
  onReady,
  purchased = false,
  claimEndpoint = '/v1/divorce/claim',
  claimTokenField = 'claim_token',
  title,
  description,
  successMessage = 'Saved to your Harbor account',
}: {
  claimToken: string;
  ready: boolean;
  onReady: () => void;
  purchased?: boolean;
  claimEndpoint?: string;
  claimTokenField?: string;
  title?: string;
  description?: string;
  successMessage?: string;
}) {
  const { isLoaded: authLoaded, isSignedIn, getToken } = useAuth();
  const { user, isLoaded: userLoaded } = useUser();
  const { signIn, setActive: setSignInActive, isLoaded: signInLoaded } = useSignIn();
  const { signUp, setActive: setSignUpActive, isLoaded: signUpLoaded } = useSignUp();
  const [flow, setFlow] = useState<Flow>(ready ? 'ready' : 'email');
  const [attempt, setAttempt] = useState<Attempt>('sign-in');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [privateInbox, setPrivateInbox] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [emailAddressId, setEmailAddressId] = useState<string | null>(null);
  const claimedSessionRef = useRef<string | null>(null);

  const claim = useCallback(async () => {
    if (!isSignedIn || !user?.id || ready) return;
    if (claimedSessionRef.current === user.id) return;
    claimedSessionRef.current = user.id;
    setFlow('claiming');
    setBusy(true);
    setError('');
    try {
      const response = await apiFetch(getToken, claimEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          [claimTokenField]: claimToken,
          email_private: privateInbox,
        }),
      });
      if (!response.ok) {
        throw new Error(
          extractApiErrorMessage(
            new ApiError(
              'Could not save application',
              response.status,
              await response.text(),
            ),
            'Could not save this application to your account.',
          ),
        );
      }
      track('Divorce Application Claimed');
      setFlow('ready');
      onReady();
    } catch (err) {
      claimedSessionRef.current = null;
      setFlow('email');
      setError(
        err instanceof Error
          ? err.message
          : 'Could not save this application to your account.',
      );
    } finally {
      setBusy(false);
    }
  }, [
    claimEndpoint,
    claimToken,
    claimTokenField,
    getToken,
    isSignedIn,
    onReady,
    privateInbox,
    ready,
    user?.id,
  ]);

  useEffect(() => {
    if (ready) setFlow('ready');
  }, [ready]);

  useEffect(() => {
    if (!authLoaded || !userLoaded || !isSignedIn || ready) return;
    const verifiedEmail = user?.primaryEmailAddress?.emailAddress ?? '';
    if (verifiedEmail) {
      setEmail(verifiedEmail);
      void claim();
    }
  }, [authLoaded, claim, isSignedIn, ready, user, userLoaded]);

  const start = async () => {
    const normalized = email.trim().toLowerCase();
    if (!normalized || !normalized.includes('@')) {
      setError('Enter a valid email address.');
      return;
    }
    setBusy(true);
    setError('');
    setCode('');
    try {
      if (isSignedIn && user) {
        const existing = user.emailAddresses.find(
          (item) => item.emailAddress.toLowerCase() === normalized,
        );
        const address = existing ?? (await user.createEmailAddress({ email: normalized }));
        await address.prepareVerification({ strategy: 'email_code' });
        setEmailAddressId(address.id);
        setAttempt('attach-email');
        setFlow('code');
        track('Divorce Account Email Code Sent', { mode: 'attach' });
        return;
      }

      if (!signInLoaded || !signUpLoaded || !signIn || !signUp) return;
      try {
        const result = await signIn.create({ identifier: normalized });
        const factor = result.supportedFirstFactors?.find(
          (item): item is typeof item & { emailAddressId: string } =>
            item.strategy === 'email_code' && 'emailAddressId' in item,
        );
        if (!factor) throw new Error('Email code sign-in is unavailable for this account.');
        await signIn.prepareFirstFactor({
          strategy: 'email_code',
          emailAddressId: factor.emailAddressId,
        });
        setAttempt('sign-in');
      } catch (err) {
        const codeValue = (err as { errors?: { code?: string }[] })?.errors?.[0]?.code;
        if (codeValue !== 'form_identifier_not_found') throw err;
        await signUp.create({ emailAddress: normalized });
        await signUp.prepareEmailAddressVerification({ strategy: 'email_code' });
        setAttempt('sign-up');
      }
      setFlow('code');
      track('Divorce Account Email Code Sent', { mode: 'account' });
    } catch (err) {
      setError(clerkMessage(err, 'Could not send an email code. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    if (!code.trim()) {
      setError('Enter the code from your email.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      if (attempt === 'attach-email') {
        if (!user || !emailAddressId) throw new Error('Email verification expired.');
        const address = user.emailAddresses.find((item) => item.id === emailAddressId);
        if (!address) throw new Error('Email verification expired.');
        const result = await address.attemptVerification({ code: code.trim() });
        if (result.verification.status !== 'verified') {
          throw new Error('That code could not be verified.');
        }
        await user.update({ primaryEmailAddressId: address.id });
        await user.reload();
        await claim();
        return;
      }
      if (attempt === 'sign-in') {
        if (!signInLoaded || !signIn) return;
        const result = await signIn.attemptFirstFactor({
          strategy: 'email_code',
          code: code.trim(),
        });
        if (result.status !== 'complete' || !result.createdSessionId) {
          throw new Error('Sign in is incomplete. Please try again.');
        }
        await setSignInActive({ session: result.createdSessionId });
      } else {
        if (!signUpLoaded || !signUp) return;
        const result = await signUp.attemptEmailAddressVerification({ code: code.trim() });
        if (result.status !== 'complete' || !result.createdSessionId) {
          throw new Error('Account setup is incomplete. Please try again.');
        }
        await setSignUpActive({ session: result.createdSessionId });
      }
      track('Divorce Account Email Verified');
    } catch (err) {
      setError(clerkMessage(err, "That code didn't work. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  if (ready || flow === 'ready') {
    return (
      <div className='mt-6 flex items-center gap-3 rounded-xl bg-primary/10 px-4 py-3 text-primary'>
        <FiCheck className='h-5 w-5 shrink-0' />
        <span className='font-semibold'>{successMessage}</span>
      </div>
    );
  }

  return (
    <div className='mt-6 rounded-xl border border-gray-200 bg-gray-50 p-4'>
      <h4 className='font-bold text-gray-900'>
        {title ??
          (purchased
            ? 'Save your purchase to your account'
            : 'Save your application before payment')}
      </h4>
      <p className='mt-1 text-sm text-gray-600'>
        {description ??
          (purchased
            ? 'Your payment is confirmed. Verify your email so you can download your forms and return on any device.'
            : 'Verify your email so you can return on any device and download your forms again.')}
      </p>

      {flow === 'code' ? (
        <div className='mt-4 space-y-3'>
          <label className='block text-sm font-medium text-gray-700'>
            Code sent to {email.trim().toLowerCase()}
          </label>
          <Input
            value={code}
            onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
            inputMode='numeric'
            autoComplete='one-time-code'
            placeholder='6-digit code'
          />
          <button
            type='button'
            onClick={() => void verify()}
            disabled={busy}
            className='w-full rounded-xl bg-primary px-4 py-3 font-semibold text-white disabled:opacity-50'
          >
            {busy ? 'Verifying…' : 'Verify and save'}
          </button>
          <button
            type='button'
            onClick={() => {
              setFlow('email');
              setCode('');
              setError('');
            }}
            className='w-full text-sm font-semibold text-primary'
          >
            Use a different email
          </button>
        </div>
      ) : flow === 'claiming' ? (
        <p className='mt-4 text-sm font-semibold text-gray-700'>Saving to your account…</p>
      ) : (
        <div className='mt-4 space-y-3'>
          <Input
            type='email'
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoComplete='email'
            placeholder='you@example.com'
          />
          <label className='flex items-start gap-2 text-sm text-gray-600'>
            <input
              type='checkbox'
              checked={privateInbox}
              onChange={(event) => setPrivateInbox(event.target.checked)}
              className='mt-1'
            />
            This inbox is private to me. Harbor may send application reminders.
          </label>
          <button
            type='button'
            onClick={() => void start()}
            disabled={busy}
            className='w-full rounded-xl bg-primary px-4 py-3 font-semibold text-white disabled:opacity-50'
          >
            {busy
              ? 'Sending code…'
              : isSignedIn
                ? 'Verify email'
                : purchased
                  ? 'Verify email and continue'
                  : 'Continue with email'}
          </button>
        </div>
      )}

      {error ? <p className='mt-3 text-sm font-medium text-red-600'>{error}</p> : null}
    </div>
  );
}
